import { useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle, ShieldAlert, HandMetal, Construction, ThumbsUp, Lightbulb,
  Leaf, Sprout, CheckCircle2, Wrench, Lock, Eye, Camera, MapPin, Loader2, Check,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";

const ICONS = {
  "Near Miss": AlertTriangle, "Hazard": ShieldAlert, "Unsafe Act": HandMetal,
  "Unsafe Condition": Construction, "Positive Spot": ThumbsUp, "Improvement Suggestion": Lightbulb,
  "Environmental Issue": Leaf, "ESG Suggestion": Sprout, "Quality Issue": CheckCircle2,
  "Maintenance Issue": Wrench, "Security Issue": Lock, "General Observation": Eye,
};
const HIGH = ["Near Miss", "Hazard", "Unsafe Act", "Security Issue"];

export default function ReportForm({ categories, sites, requireName, onSubmit }) {
  const [step, setStep] = useState(1);
  const [cat, setCat] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", reporter_name: "", site_id: sites?.[0]?.id || "", location: "" });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gps, setGps] = useState(null);
  const fileRef = useRef();

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const getLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((p) => {
      setGps({ lat: p.coords.latitude, lng: p.coords.longitude });
      setForm((f) => ({ ...f, location: `${p.coords.latitude.toFixed(4)}, ${p.coords.longitude.toFixed(4)}` }));
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      await onSubmit({
        category: cat, title: form.title, description: form.description,
        priority: HIGH.includes(cat) ? "high" : "medium",
        reporter_name: form.reporter_name || "Anonymous",
        site_id: form.site_id || null, photo, location: form.location,
        lat: gps?.lat, lng: gps?.lng,
      });
      setStep(3);
    } finally {
      setBusy(false);
    }
  };

  if (step === 3)
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-col items-center py-10 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500"><Check className="h-8 w-8" /></div>
        <h3 className="mt-5 font-heading text-xl font-bold">Report submitted</h3>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">Thank you. Your report has been logged, an action created, and the responsible manager notified.</p>
        <Button className="mt-6 rounded-full" onClick={() => { setStep(1); setCat(null); setPhoto(null); setForm({ title: "", description: "", reporter_name: "", site_id: sites?.[0]?.id || "", location: "" }); }} data-testid="report-another-btn">
          Submit another report
        </Button>
      </motion.div>
    );

  return (
    <div>
      {step === 1 && (
        <div>
          <div className="mb-4 text-sm text-muted-foreground">What would you like to report?</div>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            {categories.map((c) => {
              const Icon = ICONS[c] || Eye;
              return (
                <button key={c} onClick={() => { setCat(c); setStep(2); }}
                  data-testid={`cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                  className="group flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-4 text-center transition-all hover:-translate-y-1 hover:border-[hsl(var(--accent))] hover:shadow-md">
                  <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${HIGH.includes(c) ? "bg-red-500/10 text-red-500" : "bg-secondary text-[hsl(var(--accent))]"}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-medium leading-tight">{c}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 2 && (
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
          <button onClick={() => setStep(1)} className="text-xs font-medium text-[hsl(var(--accent))]">← Change category</button>
          <div className="flex items-center gap-2 rounded-xl bg-secondary px-3 py-2 text-sm font-medium">
            {(() => { const Icon = ICONS[cat] || Eye; return <Icon className="h-4 w-4 text-[hsl(var(--accent))]" />; })()} {cat}
          </div>

          {requireName && (
            <div className="space-y-1.5">
              <Label>Your name (optional)</Label>
              <Input value={form.reporter_name} onChange={(e) => setForm({ ...form, reporter_name: e.target.value })} placeholder="Leave blank to stay anonymous" data-testid="report-name" />
            </div>
          )}

          {sites?.length > 1 && (
            <div className="space-y-1.5">
              <Label>Site</Label>
              <select className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} data-testid="report-site">
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Brief summary" data-testid="report-title" />
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What happened? Where? Any immediate action taken?" data-testid="report-description" />
          </div>

          <div className="flex flex-wrap gap-2">
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pickPhoto} />
            <Button type="button" variant="outline" className="rounded-full" onClick={() => fileRef.current.click()} data-testid="report-photo-btn">
              <Camera className="mr-2 h-4 w-4" /> {photo ? "Photo added" : "Add photo"}
            </Button>
            <Button type="button" variant="outline" className="rounded-full" onClick={getLocation} data-testid="report-gps-btn">
              <MapPin className="mr-2 h-4 w-4" /> {gps ? "Location captured" : "Add GPS location"}
            </Button>
          </div>
          {photo && <img src={photo} alt="preview" className="max-h-48 rounded-xl border border-border object-cover" />}

          <Button disabled={!form.title || busy} onClick={submit} className="h-11 w-full rounded-full text-sm font-semibold" data-testid="report-submit-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit Report"}
          </Button>
        </motion.div>
      )}
    </div>
  );
}
